import { Box, Typography } from '@evaneos/b2b-ui';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { DossierId, TravellerTrip } from '../../../resources/dossier/types';
import { DefinitionList } from '../../../design-system/DefinitionList/DefinitionList';
import { useGetAgentByDossierId, useGetDossierData } from '../../../resources/dossier/queries';
import { SALES_PROCESS } from '../../../domain';
import { useGetDissatisfactionCategories } from '../../../resources/dissatisfaction-categories/queries';
import { useGetTestimonial } from '../../../resources/testimonial';
import { TestimonialModal } from '../components/TestimonialModal';
import { formatBudgetPerDayPerPax } from '../../../resources/dossier/operations';
import { useLocale } from '../../../shared/providers/LocalizationProvider';

import { AgentInfo } from './AgentInfo/AgentInfo';
import { PhoneNumberInfo } from './PhoneNumberInfo/PhoneNumberInfo';
import { TravellerProfileInfo } from './TravellerProfileInfo';
import { TravelInfo } from './TravelInfo/TravelInfo';
import { PreviousTripInfo } from './PreviousTripInfo';
import { AssociatedTags } from './AssociatedTags/AssociatedTags';

export function DossierSecondaryInfo() {
    const { formatMessage } = useIntl();
    const { locale } = useLocale();
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const assignedAgent = useGetAgentByDossierId(dossierId);
    const { dossier, participants, travellerInformation } = useGetDossierData(dossierId) || {};

    const [modalOpenedTestimonial, openModalTestimonial] = useState<boolean>(false);
    const [testimonialTrip, setTestimonialTrip] = useState<TravellerTrip | null>(null);

    const {
        data: testimonial,
        isLoading: isTestimonialLoading,
        error: testimonialError,
    } = useGetTestimonial(testimonialTrip?.id || '', {
        enabled: !!testimonialTrip?.id,
    });

    const { data: dissatisfactionCategories, isLoading: isDissatisfactionCategoriesLoading } =
        useGetDissatisfactionCategories(testimonialTrip?.id || null);

    const areTestimonialsLoading = isDissatisfactionCategoriesLoading || isTestimonialLoading;

    function handleOpenTestimonialModal(trip: TravellerTrip): void {
        setTestimonialTrip(trip);
        openModalTestimonial(true);
    }

    return (
        <Box
            sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                marginBottom: 2,
                paddingBottom: 2,
            }}
        >
            <Typography component="h3" variant="h4" paddingTop={1} paddingBottom={2}>
                {formatMessage({ id: 'dossier.information.navbar_title' })}
            </Typography>
            <DefinitionList>
                <AgentInfo assignedAgent={assignedAgent} dossierId={dossierId} />
                <PhoneNumberInfo project={dossier?.project} dossierId={dossierId} />
                <TravelInfo
                    project={dossier?.project}
                    dossierId={dossierId}
                    contractIssued={dossier?.contract.state !== 'not-issued'}
                />

                <TravellerProfileInfo rating={dossier?.rating} profile={participants?.profile} />
                <PreviousTripInfo
                    trips={travellerInformation?.trips}
                    onOpenTestimonialModal={handleOpenTestimonialModal}
                />
                {dossier?.process === SALES_PROCESS && <AssociatedTags />}
            </DefinitionList>

            {testimonialTrip && (
                <TestimonialModal
                    modalOpened={modalOpenedTestimonial}
                    openModal={(open) => openModalTestimonial(open)}
                    trip={testimonialTrip}
                    testimonial={testimonial || null}
                    dissatisfactionCategories={dissatisfactionCategories || null}
                    isLoading={areTestimonialsLoading}
                    error={testimonialError}
                    tripBudget={formatBudgetPerDayPerPax(
                        testimonialTrip.budgetPerDayPerPax,
                        testimonialTrip.budgetCurrency,
                        formatMessage,
                        locale,
                    )}
                />
            )}
        </Box>
    );
}
